import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Button,
	Form,
	Input,
	message,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Typography,
} from 'antd'
import createClientAdminAccessSession from 'api/clients/createClientAdminAccessSession'
import getClientAdminAccessSessions, {
	AdminAccessLogRecord,
} from 'api/clients/getClientAdminAccessSessions'
import getClientUsers from 'api/users/getClientUsers'
import { ClientUser } from 'types'

interface Props {
	clientId: string
}

const CRM_APP_V2 = 'https://v2.dashcx.com'
const LEGACY_DASHCX_APP_HOSTS = ['app.dashcx.com', 'app.whatcx.com']

const formatUserLabel = (user: ClientUser) => {
	const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
	const role =
		user.role && typeof user.role === 'object' && 'role' in user.role
			? (user.role as { role?: string }).role
			: null
	const base = name || user.email || user._id
	const withEmail = user.email && name ? `${base} (${user.email})` : base
	return role ? `${withEmail} — ${role}` : withEmail
}

const resolveLaunchUrl = (appWebsite?: string) => {
	const fromEnv = process.env.REACT_APP_CRM_APP_URL?.trim()
	if (fromEnv) {
		return fromEnv.endsWith('/') ? fromEnv : `${fromEnv}/`
	}

	const normalized = (appWebsite || '').trim().replace(/\/+$/, '')
	if (!normalized) {
		return `${CRM_APP_V2}/`
	}

	try {
		const host = new URL(
			normalized.startsWith('http') ? normalized : `https://${normalized}`
		).hostname
		if (LEGACY_DASHCX_APP_HOSTS.includes(host)) {
			return `${CRM_APP_V2}/`
		}
	} catch {
		// fall through
	}

	if (
		normalized === 'https://app.dashcx.com' ||
		normalized === 'https://app.whatcx.com'
	) {
		return `${CRM_APP_V2}/`
	}

	return `${normalized}/`
}

const AdminAccess: React.FC<Props> = ({ clientId }) => {
	const [logs, setLogs] = useState<AdminAccessLogRecord[]>([])
	const [loading, setLoading] = useState(false)
	const [creating, setCreating] = useState(false)
	const [open, setOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [size, setSize] = useState(10)
	const [total, setTotal] = useState(0)
	const [users, setUsers] = useState<ClientUser[]>([])
	const [usersLoading, setUsersLoading] = useState(false)
	const [form] = Form.useForm<{ reason: string; adminId: string }>()

	const fetchLogs = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getClientAdminAccessSessions(clientId, page, size)
			setLogs(res.records || [])
			setTotal(res.totalCount || 0)
		} finally {
			setLoading(false)
		}
	}, [clientId, page, size])

	const fetchUsers = useCallback(
		async (query = '') => {
			if (!clientId) return
			setUsersLoading(true)
			try {
				const res = await getClientUsers({
					client: clientId,
					page: 1,
					size: 500,
					query,
				})
				setUsers((res.records || []).filter(user => user.active !== false))
			} catch {
				setUsers([])
			} finally {
				setUsersLoading(false)
			}
		},
		[clientId]
	)

	useEffect(() => {
		if (!clientId) return
		fetchLogs().catch(() => undefined)
	}, [clientId, fetchLogs])

	useEffect(() => {
		if (!open) return
		fetchUsers().catch(() => undefined)
	}, [open, fetchUsers])

	const handleCreate = async () => {
		try {
			const values = await form.validateFields()
			setCreating(true)
			const res = await createClientAdminAccessSession(clientId, {
				reason: values.reason,
				adminId: values.adminId,
			})
			const launchBase = resolveLaunchUrl(res.appWebsite)
			const launchUrl = `${launchBase.replace(
				/\/$/,
				''
			)}/login?adminAccessToken=${encodeURIComponent(res.accessToken)}`
			window.open(launchUrl, '_blank', 'noopener,noreferrer')
			message.success('Admin access session created and opened in a new tab')
			form.resetFields()
			setOpen(false)
			fetchLogs().catch(() => undefined)
		} finally {
			setCreating(false)
		}
	}

	const userOptions = useMemo(
		() =>
			users.map(user => ({
				value: user._id,
				label: formatUserLabel(user),
			})),
		[users]
	)

	const columns = useMemo(
		() => [
			{
				title: 'Created',
				dataIndex: 'createdAt',
				key: 'createdAt',
				render: (value: string) => new Date(value).toLocaleString(),
			},
			{
				title: 'Requested By',
				dataIndex: 'email',
				key: 'email',
			},
			{
				title: 'Login As',
				key: 'targetUser',
				render: (_: unknown, record: AdminAccessLogRecord) => {
					const name = record.payload?.targetName
					const email = record.payload?.targetEmail
					if (name && email) return `${name} (${email})`
					return name || email || record.payload?.adminId || '-'
				},
			},
			{
				title: 'Status',
				key: 'status',
				render: (_: unknown, record: AdminAccessLogRecord) => (
					<Tag color={record.payload?.status === 'CONSUMED' ? 'green' : 'blue'}>
						{record.payload?.status || 'CREATED'}
					</Tag>
				),
			},
			{
				title: 'Reason',
				key: 'reason',
				render: (_: unknown, record: AdminAccessLogRecord) =>
					record.payload?.reason || '-',
			},
			{
				title: 'Consumed',
				key: 'consumedAt',
				render: (_: unknown, record: AdminAccessLogRecord) =>
					record.payload?.consumedAt
						? new Date(record.payload.consumedAt).toLocaleString()
						: '-',
			},
		],
		[]
	)

	return (
		<>
			<Space direction='vertical' style={{ width: '100%' }} size='middle'>
				<Space style={{ width: '100%', justifyContent: 'space-between' }}>
					<Typography.Text type='secondary'>
						Open an isolated session as any client user to verify permissions. Every
						access is logged.
					</Typography.Text>
					<Button type='primary' onClick={() => setOpen(true)}>
						Open Admin Session
					</Button>
				</Space>

				<Table
					rowKey='_id'
					loading={loading}
					columns={columns}
					dataSource={logs}
					pagination={{
						current: page,
						pageSize: size,
						total,
						onChange: (nextPage, nextSize) => {
							setPage(nextPage)
							setSize(nextSize || 10)
						},
					}}
				/>
			</Space>

			<Modal
				title='Create Admin Access Session'
				visible={open}
				onCancel={() => setOpen(false)}
				onOk={handleCreate}
				confirmLoading={creating}
				okText='Create & Open'
				destroyOnClose
			>
				<Form form={form} layout='vertical'>
					<Form.Item
						label='Login as'
						name='adminId'
						rules={[{ required: true, message: 'Select a user to login as' }]}
					>
						<Select
							showSearch
							placeholder='Select user'
							loading={usersLoading}
							options={userOptions}
							optionFilterProp='label'
						/>
					</Form.Item>
					<Form.Item
						label='Reason'
						name='reason'
						rules={[
							{ required: true, message: 'Reason is required' },
							{ max: 500, message: 'Maximum 500 characters allowed' },
						]}
					>
						<Input.TextArea
							placeholder='Describe why elevated access is needed'
							rows={4}
							maxLength={500}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	)
}

export default AdminAccess
